import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { productCategorySchema, ProductCategorySchemaInput, ProductCategorySchemaOutput } from "./utils/productCategory.schema";

import { GetProductCategoryDocument } from "__generated__/view/productcategories/graphql/queries.generated";
import { CreateOrUpdateProductCategoryDocument } from "__generated__/view/productcategories/graphql/mutations.generated";

const ProductCategoryCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const { control, handleSubmit, reset } = useForm<ProductCategorySchemaInput, unknown, ProductCategorySchemaOutput>({
		resolver: zodResolver(productCategorySchema),
		defaultValues: {
			id: null,
			name: "",
		},
	});

	const [ getProductCategory ] = useLazyQuery(GetProductCategoryDocument, {
		onCompleted: response => {
			reset({
				id: response.productCategory?.id,
				name: response.productCategory?.name,
			});
		},
	});

	const [ createOrUpdateProductCategory, { loading } ] = useMutation(CreateOrUpdateProductCategoryDocument);

	const save = (data: ProductCategorySchemaOutput) => {
		createOrUpdateProductCategory({
			variables: {
				input: data,
			},
		}).then(() => {
			navigate("/view/productcategories");
		});
	};

	useEffect(() => {
		if (id) {
			getProductCategory({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("PRODUCT_CATEGORY")}
					<Helmet title={intl.formatMessage({ id: "PRODUCT_CATEGORY" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/productcategories")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12 }}>
					<Card>
						<FormGroup
							label={Language("NAME")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="name"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default ProductCategoryCreateEdit;
